import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  MessageCircle,
  Phone,
  Mail,
  Target,
  Brain,
  Heart,
  Clock,
  Calendar,
  Activity,
  Zap,
  AlertTriangle,
  Eye,
  FileText,
  ChevronRight,
  X,
  Filter
} from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

// Document type configurations
const DOC_TYPES = {
  prematch: { 
    label: 'Pre-Partido', 
    icon: Target, 
    color: 'bg-[#D4AF37]/20 text-[#D4AF37]',
    borderColor: 'border-[#D4AF37]'
  },
  map360: { 
    label: 'Mapa 360°', 
    icon: Eye, 
    color: 'bg-cyan-500/20 text-cyan-400',
    borderColor: 'border-cyan-500'
  },
  postmatch: { 
    label: 'Post-Partido', 
    icon: Brain, 
    color: 'bg-blue-500/20 text-blue-400',
    borderColor: 'border-blue-500'
  },
  decision: { 
    label: 'Diario Decisiones', 
    icon: Activity, 
    color: 'bg-orange-500/20 text-orange-400',
    borderColor: 'border-orange-500'
  },
  checkin: { 
    label: 'Check-in Mensual', 
    icon: Calendar, 
    color: 'bg-pink-500/20 text-pink-400',
    borderColor: 'border-pink-500'
  },
  injury: { 
    label: 'Lesión', 
    icon: AlertTriangle, 
    color: 'bg-red-500/20 text-red-400',
    borderColor: 'border-red-500'
  },
  career: { 
    label: 'Actualización Carrera', 
    icon: Zap, 
    color: 'bg-purple-500/20 text-purple-400',
    borderColor: 'border-purple-500'
  },
};

function IkonsAdminPlayerDetail() {
  const navigate = useNavigate();
  const { playerId } = useParams();
  const { user } = useAuth();
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (user?.role !== 'agent') {
      navigate('/dashboard');
      return;
    }
    fetchPlayerDetail();
  }, [user, playerId, navigate]);

  const fetchPlayerDetail = async () => {
    try {
      const response = await api.get(`/ikons/admin/player/${playerId}`);
      setPlayerData(response.data);
    } catch (error) {
      console.error('Error fetching player:', error);
      toast.error('Error al cargar datos del jugador');
      navigate('/ikons/admin');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleContact = (type) => {
    const player = playerData?.player;
    if (!player) return;
    
    if (type === 'whatsapp' && player.whatsapp_number) {
      window.open(`https://wa.me/${player.whatsapp_number.replace('+', '')}`, '_blank');
    } else if (type === 'email' && player.email) {
      window.open(`mailto:${player.email}`, '_blank');
    } else if (type === 'call' && player.whatsapp_number) {
      window.open(`tel:${player.whatsapp_number}`, '_blank');
    }
  };

  // Build unified document list
  const getAllDocuments = () => {
    if (!playerData) return [];
    
    const docs = [];
    
    // Pre-Match Plans
    playerData.match_plans?.forEach(doc => {
      docs.push({
        type: 'prematch',
        date: doc.created_at || doc.match_date,
        title: doc.rival ? `vs ${doc.rival}` : `Partido ${doc.match_date}`,
        data: doc
      });
    });
    
    // Performance Maps (360)
    playerData.performance_maps?.forEach(doc => {
      docs.push({
        type: 'map360',
        date: doc.created_at || doc.match_date,
        title: `Mapa ${doc.match_date}`,
        data: doc
      });
    });
    
    // Mental Recovery (Post-Match)
    playerData.mental_recovery?.forEach(doc => {
      docs.push({
        type: 'postmatch',
        date: doc.created_at || doc.match_date,
        title: `Recuperación ${doc.match_date}`,
        data: doc
      });
    });
    
    // Decision Diaries
    playerData.decision_diaries?.forEach(doc => {
      docs.push({
        type: 'decision',
        date: doc.created_at || doc.match_date,
        title: `Decisiones ${doc.match_date}`,
        data: doc
      });
    });
    
    // Monthly Check-ins
    playerData.monthly_checkins?.forEach(doc => {
      const monthName = new Date(doc.month + '-01').toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
      docs.push({
        type: 'checkin',
        date: doc.created_at,
        title: monthName.charAt(0).toUpperCase() + monthName.slice(1),
        data: doc
      });
    });
    
    // Injury Reports
    playerData.injury_reports?.forEach(doc => {
      docs.push({
        type: 'injury',
        date: doc.reported_at,
        title: doc.injury_type,
        data: doc
      });
    });
    
    // Career Updates
    playerData.career_updates?.forEach(doc => {
      docs.push({
        type: 'career',
        date: doc.reported_at,
        title: doc.update_type,
        data: doc
      });
    });
    
    // Sort by date descending
    return docs.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const documents = getAllDocuments();
  const filteredDocs = filter === 'all' ? documents : documents.filter(d => d.type === filter);

  // Count documents by type
  const docCounts = documents.reduce((acc, doc) => {
    acc[doc.type] = (acc[doc.type] || 0) + 1;
    return acc;
  }, {});

  if (user?.role !== 'agent') return null;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!playerData) return null;

  const { player } = playerData;

  // Render document detail modal
  const renderDocumentDetail = () => {
    if (!selectedDoc) return null;
    
    const config = DOC_TYPES[selectedDoc.type];
    const Icon = config.icon;
    
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-end lg:items-center justify-center">
        <div className="w-full max-w-2xl max-h-[90vh] bg-[#121212] rounded-t-2xl lg:rounded-2xl overflow-hidden flex flex-col">
          {/* Modal Header */}
          <div className={`p-4 border-b border-white/10 flex items-center gap-3 border-l-4 ${config.borderColor}`}>
            <div className={`w-10 h-10 rounded-full ${config.color} flex items-center justify-center`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-white truncate">{config.label}</h2>
              <p className="text-xs text-zinc-500">{formatDate(selectedDoc.date)}</p>
            </div>
            <button 
              onClick={() => setSelectedDoc(null)}
              className="p-2 text-zinc-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Modal Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {renderDocumentContent(selectedDoc)}
          </div>
        </div>
      </div>
    );
  };

  // Render content based on document type
  const renderDocumentContent = (doc) => {
    const { type, data } = doc;
    
    const Field = ({ label, value, highlight = false }) => {
      if (!value) return null;
      return (
        <div className={`p-3 rounded-lg ${highlight ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' : 'bg-zinc-900'}`}>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{label}</p>
          <p className={`text-sm ${highlight ? 'text-white font-medium' : 'text-zinc-300'}`}>{value}</p>
        </div>
      );
    };

    const RatingDisplay = ({ items }) => (
      <div className="grid grid-cols-5 gap-2">
        {items.map(({ key, label, value }) => (
          <div key={key} className="text-center">
            <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center text-sm font-bold ${
              value >= 4 ? 'bg-green-500/20 text-green-400' :
              value >= 3 ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              {value || 0}
            </div>
            <p className="text-xs text-zinc-500 mt-1">{label}</p>
          </div>
        ))}
      </div>
    );
    
    switch (type) {
      case 'prematch':
        return (
          <>
            {data.rival && <Field label="Rival" value={data.rival} highlight />}
            <Field label="Objetivo Principal" value={data.main_objective} highlight />
            <Field label="Acción Clave" value={data.key_action} />
            <Field label="Gatillo Mental" value={data.mental_trigger} />
            {data.confidence_phrase && (
              <div className="bg-zinc-900 p-4 rounded-lg border-l-4 border-[#D4AF37]">
                <p className="text-xs text-[#D4AF37] uppercase tracking-wider mb-1">Frase de Confianza</p>
                <p className="text-white italic">"{data.confidence_phrase}"</p>
              </div>
            )}
          </>
        );
        
      case 'map360':
        return (
          <>
            <p className="text-sm text-zinc-400 mb-3">Estado previo al partido:</p>
            <RatingDisplay items={[
              { key: 'physical_energy', label: 'Energía', value: data.physical_energy },
              { key: 'mental_clarity', label: 'Mental', value: data.mental_clarity },
              { key: 'emotional_state', label: 'Emocional', value: data.emotional_state },
              { key: 'confidence', label: 'Confianza', value: data.confidence },
              { key: 'competitive_level', label: 'Competitivo', value: data.competitive_level },
            ]} />
          </>
        );
        
      case 'postmatch':
        return (
          <>
            <Field label="Sentimiento Actual" value={data.current_feeling} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Field label="Hechos (Objetivo)" value={data.facts} />
              <Field label="Historia (Interpretación)" value={data.story} />
            </div>
            <Field label="Aprendizaje" value={data.learning} highlight />
            {data.closing_phrase && (
              <div className="bg-zinc-900 p-4 rounded-lg border-l-4 border-blue-500">
                <p className="text-xs text-blue-400 uppercase tracking-wider mb-1">Frase de Cierre</p>
                <p className="text-white italic">"{data.closing_phrase}"</p>
              </div>
            )}
            <Field label="Foco de Trabajo" value={data.work_focus} />
          </>
        );
        
      case 'decision':
        return (
          <>
            {data.decisions?.map((decision, i) => (
              <div key={i} className="bg-zinc-900 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-orange-400 uppercase tracking-wider">Decisión {i + 1}</p>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    decision.was_correct ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                  }`}>
                    {decision.was_correct ? 'Correcta' : 'Incorrecta'}
                  </span>
                </div>
                {decision.situation && <p className="text-sm text-zinc-300"><strong>Situación:</strong> {decision.situation}</p>}
                {decision.decision_made && <p className="text-sm text-zinc-300"><strong>Decisión:</strong> {decision.decision_made}</p>}
                {decision.reason && <p className="text-sm text-zinc-400"><strong>Razón:</strong> {decision.reason}</p>}
              </div>
            ))}
            <Field label="Patrón Detectado" value={data.pattern_detected} highlight />
            <Field label="Ajuste Próximo Partido" value={data.next_match_adjustment} />
          </>
        );
        
      case 'checkin':
        return (
          <>
            <div className="flex items-center justify-center gap-4 p-4 bg-zinc-900 rounded-lg">
              <div className="text-center">
                <p className="text-4xl font-bold text-white">{data.general_rating}</p>
                <p className="text-xs text-zinc-500">de 5</p>
              </div>
              <p className="text-zinc-400">Valoración General del Mes</p>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm font-medium text-[#CCFF00]">Rendimiento</p>
              <Field label="Lo Mejor del Mes" value={data.best_of_month} />
              <Field label="Lo Más Difícil" value={data.most_difficult} />
              <Field label="Valorado por Staff" value={data.valued_by_staff} />
              <Field label="Satisfecho con Minutos" value={data.satisfied_with_minutes} />
            </div>
            
            <div className="space-y-2">
              <p className="text-sm font-medium text-blue-400">Estado Mental</p>
              <Field label="Haciendo Bien" value={data.doing_well} />
              <Field label="Necesita Mejorar" value={data.needs_improvement} />
              <Field label="Gestión de Frustración" value={data.frustration_management} />
              <Field label="Momentos de Inseguridad" value={data.moments_of_insecurity} />
              <Field label="Fuentes de Confianza" value={data.confidence_sources} />
            </div>
            
            <div className="space-y-2">
              <p className="text-sm font-medium text-green-400">Estado Físico</p>
              <Field label="Sensación Física" value={data.physical_feeling} />
              <Field label="Molestias/Alertas" value={data.discomforts_alerts} />
              <Field label="Descanso y Recuperación" value={data.rest_recovery} />
            </div>
            
            <div className="space-y-2">
              <p className="text-sm font-medium text-purple-400">Entorno</p>
              <Field label="Relación con Staff" value={data.relationship_staff} />
              <Field label="Relación con Compañeros" value={data.relationship_teammates} />
              <Field label="Impacto del Entorno" value={data.environment_impact} />
            </div>
            
            <div className="space-y-2">
              <p className="text-sm font-medium text-pink-400">Objetivos</p>
              <Field label="Objetivo Próximo Mes" value={data.next_month_goal} highlight />
              <Field label="Hábito a Mejorar" value={data.habit_to_improve} />
              <Field label="Necesita de la Agencia" value={data.needs_from_agency} highlight />
            </div>
          </>
        );
        
      case 'injury':
        return (
          <>
            <div className={`p-4 rounded-lg text-center ${
              data.severity === 'Crítica' ? 'bg-red-700/20' :
              data.severity === 'Grave' ? 'bg-red-500/20' :
              data.severity === 'Moderada' ? 'bg-orange-500/20' :
              'bg-yellow-500/20'
            }`}>
              <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Severidad</p>
              <p className={`text-2xl font-bold ${
                data.severity === 'Crítica' ? 'text-red-300' :
                data.severity === 'Grave' ? 'text-red-400' :
                data.severity === 'Moderada' ? 'text-orange-400' :
                'text-yellow-400'
              }`}>{data.severity}</p>
            </div>
            <Field label="Tipo de Lesión" value={data.injury_type} highlight />
            <Field label="Descripción" value={data.description} />
          </>
        );
        
      case 'career':
        return (
          <>
            <Field label="Tipo de Novedad" value={data.update_type} highlight />
            <Field label="Descripción" value={data.description} />
          </>
        );
        
      default:
        return <p className="text-zinc-500">Sin contenido disponible</p>;
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#F8F9FA]" data-testid="player-detail-page">
        
        {/* Header */}
        <div className="sticky top-14 lg:top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/ikons/admin')} className="p-2 -ml-2 text-[#808080] active:scale-95">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A67C00] flex items-center justify-center text-white font-bold text-lg">
                {player?.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-bold text-[#4F4F4F] truncate">{player?.name}</h1>
                <p className="text-xs text-[#808080] truncate">{player?.email}</p>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex gap-2 mt-3">
              <Button 
                size="sm"
                className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white"
                onClick={() => handleContact('whatsapp')}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
              <Button 
                size="sm"
                variant="outline"
                className="h-10 border-gray-300 text-[#4F4F4F]"
                onClick={() => handleContact('call')}
              >
                <Phone className="w-4 h-4" />
              </Button>
              <Button 
                size="sm"
                variant="outline"
                className="h-10 border-gray-300 text-[#4F4F4F]"
                onClick={() => handleContact('email')}
              >
                <Mail className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  filter === 'all' 
                    ? 'bg-[#D4AF37] text-white' 
                    : 'bg-gray-100 text-[#808080] hover:bg-gray-200'
                }`}
              >
                Todos ({documents.length})
              </button>
              {Object.entries(DOC_TYPES).map(([key, config]) => {
                const count = docCounts[key] || 0;
                if (count === 0) return null;
                const Icon = config.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                      filter === key 
                        ? 'bg-[#4F4F4F] text-white' 
                        : 'bg-gray-100 text-[#808080] hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {config.label} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Document List */}
        <div className="px-4 py-4 max-w-4xl mx-auto pb-24">
          {filteredDocs.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-[#808080]">
                {filter === 'all' 
                  ? 'Este jugador aún no ha completado documentos'
                  : `No hay documentos de tipo "${DOC_TYPES[filter]?.label}"`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDocs.map((doc, i) => {
                const config = DOC_TYPES[doc.type];
                const Icon = config.icon;
                return (
                  <button
                    key={`${doc.type}-${i}`}
                    onClick={() => setSelectedDoc(doc)}
                    className={`w-full bg-white rounded-xl p-4 border border-gray-200 hover:border-[#D4AF37] hover:shadow-md transition-all text-left flex items-center gap-4 active:scale-[0.99]`}
                  >
                    <div className={`w-12 h-12 rounded-full ${config.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#4F4F4F] truncate">{doc.title}</p>
                      <p className="text-xs text-[#808080]">{config.label}</p>
                      <p className="text-xs text-[#808080] mt-1">{formatDate(doc.date)}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Document Detail Modal */}
        {selectedDoc && renderDocumentDetail()}
      </div>
    </Layout>
  );
}

export default IkonsAdminPlayerDetail;
